import type { TurnTimeline, TurnMark } from '@voicer/shared';

interface Span {
  label: string;
  from: TurnMark;
  to: TurnMark;
  color: string;
}

const SPANS: Span[] = [
  { label: 'User speech', from: 'speechStart', to: 'speechEnd', color: '#3ccf8e' },
  { label: 'STT queue', from: 'sttQueued', to: 'sttSent', color: '#6b7280' },
  { label: 'STT', from: 'sttSent', to: 'sttDone', color: '#22c55e' },
  { label: 'End-of-turn check', from: 'eotCheckStart', to: 'eotCheckDone', color: '#a3e635' },
  { label: 'Compile context', from: 'compileStart', to: 'compileDone', color: '#c084fc' },
  { label: 'LLM queue', from: 'llmQueued', to: 'llmStart', color: '#6b7280' },
  { label: 'LLM to first token', from: 'llmStart', to: 'llmFirstToken', color: '#f59e0b' },
  { label: 'LLM generation', from: 'llmFirstToken', to: 'llmDone', color: '#fbbf24' },
  { label: 'First TTS chunk', from: 'ttsFirstSent', to: 'ttsFirstDone', color: '#60a5fa' },
  { label: 'Playback', from: 'firstAudioOut', to: 'playbackEnd', color: '#5aa9ff' },
];

export function Waterfall({ turn }: { turn: TurnTimeline }) {
  const m = turn.marks;
  const values = Object.values(m).filter((v): v is number => typeof v === 'number');
  if (!values.length) return <div className="dim">no marks</div>;
  const t0 = Math.min(...values);
  const t1 = Math.max(...values);
  const total = Math.max(1, t1 - t0);
  const pct = (t: number) => `${((t - t0) / total) * 100}%`;
  const ttfa = m.speechEnd !== undefined && m.firstAudioOut !== undefined ? m.firstAudioOut - m.speechEnd : m.turnCommitted !== undefined && m.firstAudioOut !== undefined ? m.firstAudioOut - m.turnCommitted : null;

  return (
    <div className="waterfall">
      {SPANS.map((s) => {
        const a = m[s.from];
        const b = m[s.to];
        if (a === undefined || b === undefined) return null;
        return (
          <div className="wf-row" key={s.label}>
            <span>{s.label}</span>
            <div className="wf-track">
              <div className="wf-bar" style={{ left: pct(a), width: `calc(${((b - a) / total) * 100}% + 1px)`, background: s.color }} />
            </div>
            <span className="dim">{b - a} ms</span>
          </div>
        );
      })}
      <div className="wf-row">
        <span>Markers</span>
        <div className="wf-track" style={{ background: 'transparent' }}>
          {m.turnCommitted !== undefined && <div className="wf-mark" title="turn committed" style={{ left: pct(m.turnCommitted) }} />}
          {m.firstAudioOut !== undefined && <div className="wf-mark" title="first audio out" style={{ left: pct(m.firstAudioOut), background: '#5aa9ff' }} />}
          {m.interrupted !== undefined && <div className="wf-mark" title="interrupted" style={{ left: pct(m.interrupted), background: '#ff6b6b' }} />}
        </div>
        <span className="dim">{total} ms</span>
      </div>
      <div className="row dim" style={{ fontSize: 11, marginTop: 6 }}>
        {ttfa !== null && <span>time to first audio {ttfa} ms</span>}
        <span>{turn.genTokens} tok</span>
        {turn.tokensPerSec && <span>{turn.tokensPerSec.toFixed(1)} tok/s</span>}
        <span>{turn.llmSegments} LLM segments</span>
        <span>{turn.ttsChunks} TTS chunks ({turn.ttsMsTotal} ms)</span>
        <span>prompt {turn.promptTokens} tok</span>
        {turn.perf && <span>kobold prefill {turn.perf.last_process.toFixed(2)}s / eval {turn.perf.last_eval.toFixed(2)}s</span>}
        {turn.interrupted && <span style={{ color: 'var(--bad)' }}>interrupted</span>}
      </div>
    </div>
  );
}
